import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, MessageSquareText, ShieldCheck, X } from 'lucide-react';
import { STAGES, REASONS, FEATURES } from '../../config/productInsights';
import { EVENTS, insight } from '../../utils/productInsights';
import { parseCrawlLocale } from '../../utils/localePath';
import { submitProductFeedback } from '../../api/productFeedbackClient';
import type { FeedbackFeature, FeedbackReason, FeedbackStage, ProductFeedbackInput } from '../../types/productFeedback';
import { Button } from './Button';

/** User-initiated, non-modal research. No exit interception or recording of form keystrokes. */
export default function ProductFeedback() {
  const { pathname } = useLocation();
  const surface = parseCrawlLocale(pathname).path.slice(1);
  if (!['start', 'agentic', 'pricing'].includes(surface)) return null;
  return <FeedbackForm key={surface} surface={surface as ProductFeedbackInput['surface']} />;
}

const FEEDBACK_PREF_KEY = 'aiq_feedback_pref';
const FEEDBACK_PREF_MAX_AGE_MS = 30 * 60 * 1000;

function readFeedbackPref(): { stage?: FeedbackStage } {
  try {
    const raw = sessionStorage.getItem(FEEDBACK_PREF_KEY);
    sessionStorage.removeItem(FEEDBACK_PREF_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || !Number.isFinite(parsed.at) || Date.now() - parsed.at > FEEDBACK_PREF_MAX_AGE_MS) return {};
    if (STAGES.includes(parsed.stage)) return { stage: parsed.stage as FeedbackStage };
  } catch { /* Best effort. */ }
  return {};
}

function FeedbackForm({ surface }: { surface: ProductFeedbackInput['surface'] }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<FeedbackStage>(surface === 'pricing' ? 'purchase' : surface === 'start' ? 'registration' : 'ai');
  const [reason, setReason] = useState<FeedbackReason | ''>('');
  const [feature, setFeature] = useState<FeedbackFeature | ''>('');
  const [detail, setDetail] = useState('');
  const [nickname, setNickname] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const attempt = useRef<{ id: string; at: number } | null>(null);
  const busy = useRef(false);
  const openerRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const restoreFocusRef = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (open) {
      headingRef.current?.focus();
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      openerRef.current?.focus();
    }
  }, [open]);
  const onOpen = (source: 'user' | 'paywall_nudge' = 'user') => {
    const pref = readFeedbackPref();
    if (pref.stage) setStage(pref.stage);
    if (!attempt.current) attempt.current = { id: crypto.randomUUID(), at: Date.now() };
    setOpen(true);
    insight(EVENTS.FEEDBACK_OPENED, { surface, source });
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };
  useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === '#product-feedback') onOpen('paywall_nudge');
    };
    const onCustom = () => onOpen('paywall_nudge');
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    window.addEventListener('aiq:open-product-feedback', onCustom as EventListener);
    return () => {
      window.removeEventListener('hashchange', openFromHash);
      window.removeEventListener('aiq:open-product-feedback', onCustom as EventListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once on mount/hash; onOpen closes over surface
  }, [surface]);
  const onClose = () => {
    restoreFocusRef.current = true;
    setOpen(false);
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!attempt.current || busy.current || !reason || (reason === 'missing_feature' && !feature)) return;
    busy.current = true;
    setStatus('sending');
    try {
      await submitProductFeedback({
        submissionId: attempt.current.id, formLoadTimestamp: attempt.current.at,
        stage, reason, feature: reason === 'missing_feature' ? feature || undefined : undefined,
        detail, nickname, surface, lang: i18n.language.startsWith('pl') ? 'pl' : 'en',
      });
      setStatus('sent');
      setDetail('');
      insight(EVENTS.FEEDBACK_SENT, { surface, stage, reason, feature: reason === 'missing_feature' ? feature : undefined });
    } catch { setStatus('error'); }
    finally { busy.current = false; }
  };
  const fieldClass = 'glass-input mt-1.5 min-h-[48px] text-sm disabled:cursor-not-allowed disabled:opacity-60';
  const labelClass = 'block text-sm font-medium leading-snug text-gray-200';
  const formId = `product-feedback-${surface}`;
  return (
    <section
      ref={sectionRef}
      id="product-feedback"
      className="mx-auto mb-10 mt-6 w-full max-w-3xl px-3 sm:mt-8 sm:px-6"
      aria-label={t('product_feedback.title')}
    >
      {!open ? (
        <button
          ref={openerRef}
          type="button"
          onClick={() => onOpen('user')}
          aria-expanded="false"
          aria-controls={formId}
          className="group mx-auto flex min-h-[48px] max-w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-left text-sm font-medium text-gray-300 shadow-lg shadow-black/10 transition-[color,background-color,border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-primary/35 hover:bg-primary/[0.06] hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10 text-primary-light">
            <MessageSquareText className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 break-words">{t('product_feedback.open')}</span>
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-500 transition-transform group-hover:translate-x-0.5 group-hover:text-primary-light" aria-hidden="true" />
        </button>
      ) : (
        <div id={formId} className="overflow-hidden rounded-2xl border border-white/10 bg-gray-900/75 shadow-2xl shadow-black/25 animate-fade-in">
          <div className="flex items-start gap-3 border-b border-white/10 px-4 py-4 sm:px-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary-light">
              <MessageSquareText className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <h2 ref={headingRef} tabIndex={-1} className="text-base font-bold leading-snug text-white outline-none sm:text-lg">{t('product_feedback.title')}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('product_feedback.close')}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-transparent text-gray-400 transition-colors hover:border-white/10 hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {status === 'sent' ? (
            <div className="flex flex-col items-center px-5 py-8 text-center sm:px-8">
              <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-950/30 text-emerald-300">
                <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
              </span>
              <p role="status" className="max-w-lg text-sm leading-relaxed text-gray-200">{t('product_feedback.thanks')}</p>
              <Button type="button" variant="ghost" size="sm" className="mt-5" onClick={onClose}>
                {t('product_feedback.close')}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="px-4 py-5 sm:px-6 sm:py-6">
              <fieldset disabled={status === 'sending'} className="space-y-5">
                <div className="grid min-w-0 gap-4 sm:grid-cols-2">
                  <label className={labelClass}>
                    {t('product_feedback.stage')}
                    <select value={stage} onChange={e => setStage(e.target.value as FeedbackStage)} className={fieldClass}>
                      {STAGES.map(value => <option key={value} value={value}>{t(`product_feedback.stages.${value}`)}</option>)}
                    </select>
                  </label>
                  <label className={labelClass}>
                    {t('product_feedback.reason')}
                    <select required value={reason} onChange={e => { setReason(e.target.value as FeedbackReason | ''); setStatus('idle'); }} className={fieldClass}>
                      <option value="">{t('product_feedback.choose')}</option>
                      {REASONS.map(value => <option key={value} value={value}>{t(`product_feedback.reasons.${value}`)}</option>)}
                    </select>
                  </label>
                </div>

                {reason === 'missing_feature' && (
                  <label className={labelClass}>
                    {t('product_feedback.feature')}
                    <select required value={feature} onChange={e => setFeature(e.target.value as FeedbackFeature | '')} className={fieldClass}>
                      <option value="">{t('product_feedback.choose')}</option>
                      {FEATURES.map(value => <option key={value} value={value}>{t(`product_feedback.features.${value}`)}</option>)}
                    </select>
                  </label>
                )}

                <label className={labelClass}>
                  <span className="flex items-start justify-between gap-3">
                    <span>{t('product_feedback.detail')}</span>
                    <span className="shrink-0 text-xs font-normal tabular-nums text-gray-500" aria-hidden="true">{detail.length}/500</span>
                  </span>
                  <textarea
                    maxLength={500}
                    rows={4}
                    value={detail}
                    onChange={e => { setDetail(e.target.value); setStatus('idle'); }}
                    className={`${fieldClass} min-h-[112px] resize-y leading-relaxed`}
                  />
                </label>

                <input aria-hidden="true" tabIndex={-1} autoComplete="off" className="hidden" value={nickname} onChange={e => setNickname(e.target.value)} name="nickname" />

                <div className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-black/20 p-3 text-xs leading-relaxed text-gray-400">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary-light/80" aria-hidden="true" />
                  <p>
                    {t('product_feedback.privacy')}{' '}
                    <Link className="agentic-link font-medium" to="/privacy">{t('product_feedback.privacy_link')}</Link>
                  </p>
                </div>

                {status === 'error' && (
                  <p role="alert" className="agentic-status-bad rounded-xl border px-3 py-2.5 text-sm leading-relaxed">{t('product_feedback.error')}</p>
                )}

                <div className="flex flex-col-reverse gap-2 border-t border-white/10 pt-4 sm:flex-row sm:justify-end">
                  <Button type="button" variant="ghost" size="sm" className="w-full sm:w-auto" onClick={onClose}>
                    {t('product_feedback.close')}
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    allowWrap
                    className="w-full sm:w-auto sm:min-w-[10rem]"
                    isLoading={status === 'sending'}
                    aria-label={status === 'sending' ? t('common.sending') : undefined}
                  >
                    {t('product_feedback.submit')}
                  </Button>
                </div>
              </fieldset>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
